import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { searchDocs } from '../content'
import { useManualLocale } from '../i18n/LocaleContext'
import { manualUi } from '../i18n/messages'
import { manualPagePath } from '../paths'

interface SearchDialogProps {
  open: boolean
  onClose: () => void
}

export function SearchDialog({ open, onClose }: SearchDialogProps) {
  const { locale } = useManualLocale()
  const ui = manualUi(locale)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const hits = useMemo(() => searchDocs(query, locale), [query, locale])

  useEffect(() => {
    if (!open) return
    setQuery('')
    const t = window.setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="search-overlay" role="presentation" onClick={onClose}>
      <div
        className="search-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={ui.searchDialog}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-input-row">
          <svg viewBox="0 0 24 24" aria-hidden="true" className="search-icon">
            <path
              fill="currentColor"
              d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
            />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={ui.searchPlaceholder}
            aria-label={ui.searchLabel}
          />
          <kbd>Esc</kbd>
        </div>
        <div className="search-results">
          {!query.trim() ? (
            <p className="search-empty">{ui.typeToSearch}</p>
          ) : hits.length === 0 ? (
            <p className="search-empty">{ui.noMatches.replace('{query}', query.trim())}</p>
          ) : (
            <ul>
              {hits.map((hit, i) => (
                <li key={`${hit.pageId}-${hit.sectionId ?? i}-${i}`}>
                  <Link
                    to={
                      hit.sectionId
                        ? manualPagePath(hit.pageId, hit.sectionId)
                        : manualPagePath(hit.pageId)
                    }
                    onClick={onClose}
                  >
                    <span className="search-hit-meta">
                      {hit.category}
                      {hit.sectionTitle ? ` · ${hit.sectionTitle}` : ''}
                    </span>
                    <span className="search-hit-title">{hit.pageTitle}</span>
                    <span className="search-hit-snippet">{hit.snippet}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
